import { Inject, Injectable } from '@angular/core';
import { UIRouter } from '@uirouter/core';
import { fromEvent, Subscription } from 'rxjs';

import { CONFIG_TOKEN } from 'core/constants';
import { IXMOptions } from 'core/interfaces';
import { XmStoreUtil } from '../store';
import { take, timeout } from 'rxjs/operators';
import { HttpError } from 'services/http/error';

@Injectable({
    providedIn: 'root'
})
export class AbTestService {
    public tests: AbTest[] = [];
    private config: IXMOptions;
    private subscriptions: Subscription[] = [];
    private uiRouter: UIRouter;

    constructor(@Inject(CONFIG_TOKEN) config: IXMOptions, uiRouter: UIRouter) {
        Object.assign(this, { config, uiRouter });
        window.logAbTests = false;
    }

    public loadTarget(): Promise<string> {
        if (!this.config.BLEEDING_EDGE_CORPORATE_SOCIAL_RESPONSIBILITY) {
            return Promise.resolve('');
        }

        this.logAbTestData(`*** Adobe _satellite => ${Boolean(window._satellite)}`);
        this.logAbTestData(`*** AdobeTeamUtils => ${Boolean(this.adobeUtilsExist)}`);

        if (this.adobeUtilsExist) {
            return this.setupRouterListener();
        } else {
            // a timeout is already set within datalayer launch (default is 1sec)
            return fromEvent(document, 'c-tracking-launch-loaded').pipe(
                timeout(500), // safe-check in case we never get the event emitted
                take(1)
            ).toPromise().then(() => {
                // sanity check once more
                if (this.adobeUtilsExist) {
                    return this.setupRouterListener();
                } else {
                    throw new Error('utils not found');
                }
            }).catch(() => {
                this.logAbTestData('Adobe Target Library Not Found');

                return Promise.resolve('Adobe Target Library Not Found');
            });
        }
    }

    public getTestVersion(testName: string): string {
        const test: AbTest = this.lookupTest(testName);

        return test && test.version && test.version.toUpperCase();
    }

    public getTests(): Promise<string> {
        return new Promise((resolve: Function) => {
            try {
                window.AdobeTeamUtils.getPrefetchedOfferPromise({ mboxList: window.AdobeTeamUtils.getCurrentViewMboxList() }).then((response: AbTestOfferContent[]) => {
                    this.tests = response;
                    this.logAbTestData(`AbTests processed responses: ${JSON.stringify(this.tests)}`);
                    resolve('done');
                }).catch((promiseError: HttpError) => {
                    this.logAbTestData(`AbTests error: ${JSON.stringify(promiseError)}`);
                    resolve(promiseError);
                });
            } catch (error) {
                this.logAbTestData(`JS error: ${JSON.stringify(error)}`);
                resolve(error);
            }
        });
    }

    private get adobeUtilsExist(): boolean {
        return Boolean(window.AdobeTeamUtils && typeof window.AdobeTeamUtils.getPrefetchedOfferPromise === 'function' && typeof window.AdobeTeamUtils.getCurrentViewMboxList === 'function');
    }

    private setupRouterListener(): Promise<string> {
        this.subscriptions.push(XmStoreUtil.subscribe(this.uiRouter.globals.success$, () => this.getTests()));

        return this.getTests();
    }

    private lookupTest(testName: string): AbTest {
        const foundTests: AbTest = this.tests.find((test: AbTest) => test && test.testName === testName);
        this.logAbTestData(`AbTests search string: ${testName}, found tests: ${JSON.stringify(foundTests)}`);

        return foundTests;
    }

    private logAbTestData(data: string): void {
        if (window.logAbTests) {
            /* eslint-disable  no-console */
            console.log(data);
            /* eslint-enable  no-console */
        }
    }
}
